import React, { useEffect, useState } from "react";
import { getAllFeatures } from "../../../Services/FeaturesApi";
import { 
  Paper, Typography, Box, CircularProgress, 
  Card, CardContent, Grid, Chip, Avatar,
  Divider, Container, Alert
} from "@mui/material";
import FeaturedPlayListIcon from "@mui/icons-material/FeaturedPlayList";
import KeyIcon from "@mui/icons-material/Key";
import CategoryIcon from "@mui/icons-material/Category";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

function FeaturesList() {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        const res = await getAllFeatures();
        setFeatures(res?.features || []);
      } catch (e) {
        console.error("❌ خطأ في جلب الخصائص:", e);
        setFeatures([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFeatures();
  }, []);

  // تحديد لون بناءً على النوع
  const getTypeColor = (type) => {
    const colors = {
      'boolean': 'success',
      'number': 'primary',
      'text': 'secondary',
      'select': 'warning',
      'default': 'default'
    };
    return colors[type?.toLowerCase()] || colors.default;
  };

  // تحديد أيقونة بناءً على النوع
  const getTypeIcon = (type) => {
    switch(type?.toLowerCase()) {
      case 'boolean':
        return <CheckCircleIcon />;
      case 'number':
        return <CategoryIcon />;
      default:
        return <FeaturedPlayListIcon />;
    }
  };

  return (
    <Container maxWidth="xl">
      <Card elevation={3} sx={{ mb: 3, background: 'linear-gradient(135deg, #43cea2 0%, #185a9d 100%)' }}>
        <CardContent>
          <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <FeaturedPlayListIcon sx={{ fontSize: 40 }} />
            الخصائص المتاحة ({features.length})
          </Typography>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ ml: 2 }}>جاري تحميل الخصائص...</Typography>
        </Box>
      ) : features.length === 0 ? (
        <Alert 
          severity="info" 
          icon={<ErrorOutlineIcon fontSize="large" />}
          sx={{ 
            fontSize: '1.1rem',
            py: 3,
            borderRadius: 2
          }}
        >
          لا توجد خصائص متاحة حالياً
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {features.map((f, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={f.id}>
              <Card 
                elevation={3}
                sx={{ 
                  height: '100%',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 8,
                  },
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'visible'
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: -15,
                    right: 20,
                    bgcolor: 'white',
                    borderRadius: '50%',
                    p: 1,
                    boxShadow: 3
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: `${getTypeColor(f.type)}.main`,
                      width: 50,
                      height: 50
                    }}
                  >
                    {getTypeIcon(f.type)}
                  </Avatar>
                </Box>

                <CardContent sx={{ pt: 5 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 'bold',
                      color: '#333',
                      mb: 1,
                      minHeight: 64,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {f.name}
                  </Typography>

                  <Divider sx={{ my: 1.5 }} />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <KeyIcon sx={{ color: '#666', fontSize: 20 }} />
                      <Typography variant="body2" color="textSecondary">
                        المفتاح:
                      </Typography>
                      <Chip 
                        label={f.key} 
                        size="small" 
                        variant="outlined"
                        sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CategoryIcon sx={{ color: '#666', fontSize: 20 }} />
                      <Typography variant="body2" color="textSecondary">
                        النوع:
                      </Typography>
                      <Chip 
                        label={f.type} 
                        size="small"
                        color={getTypeColor(f.type)}
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>

                    {f.description && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="caption" color="textSecondary">
                          {f.description}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>

                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: 4,
                    bgcolor: `${getTypeColor(f.type)}.main`,
                    borderRadius: '0 0 8px 8px'
                  }}
                />
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
}

export default FeaturesList;
